"use client";

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { ArrowUpDown, ArrowUp, ArrowDown, PieChartIcon, TableIcon } from "lucide-react";
import { CLASSIFICATION_COLORS, REPLY_OUTCOME_CONFIG } from "../constants";
import type { ClassificationData, CategoryPerformanceRow, ReplyOutcome } from "../types";

type PerfSortKey = keyof CategoryPerformanceRow;
type SortDir = "asc" | "desc";

interface ClassificationBreakdownProps {
  data: ClassificationData[];
  categoryPerformance?: CategoryPerformanceRow[];
}

function buildChartConfig(data: ClassificationData[]) {
  const config: Record<string, { label: string; color: string }> = {};
  for (let i = 0; i < data.length; i++) {
    config[data[i].label] = {
      label: data[i].label,
      color: CLASSIFICATION_COLORS[i % CLASSIFICATION_COLORS.length],
    };
  }
  return config;
}

const OUTCOME_KEYS: { key: keyof CategoryPerformanceRow; outcome: ReplyOutcome }[] = [
  { key: "autoSent", outcome: "sent" },
  { key: "draft", outcome: "draft" },
  { key: "handover", outcome: "escalated" },
  { key: "skipped", outcome: "skipped" },
];

function OutcomeBar({ perf }: { perf: CategoryPerformanceRow }) {
  const total = perf.total || 1;
  const accounted = OUTCOME_KEYS.reduce((sum, { key }) => sum + (perf[key] as number), 0);
  const other = total - accounted;

  return (
    <div className="flex h-2 w-full rounded-full overflow-hidden bg-muted">
      {OUTCOME_KEYS.map(({ key, outcome }) => {
        const value = perf[key] as number;
        const pct = (value / total) * 100;
        if (pct < 1) return null;
        return (
          <div
            key={key}
            className="h-full cursor-pointer"
            style={{
              width: `${pct}%`,
              backgroundColor: REPLY_OUTCOME_CONFIG[outcome].color,
            }}
            title={`${REPLY_OUTCOME_CONFIG[outcome].label}: ${value} (${Math.round(pct)}%)`}
          />
        );
      })}
      {other > 0 && (
        <div
          className="h-full cursor-pointer"
          style={{
            width: `${(other / total) * 100}%`,
            backgroundColor: "#d1d5db",
          }}
          title={`Other (error, dry run, unprocessed): ${other} (${Math.round((other / total) * 100)}%)`}
        />
      )}
    </div>
  );
}

function ChartView({ data, categoryPerformance }: ClassificationBreakdownProps) {
  const chartConfig = buildChartConfig(data);

  const perfMap = new Map<string, CategoryPerformanceRow>();
  if (categoryPerformance) {
    for (const row of categoryPerformance) {
      perfMap.set(row.category, row);
    }
  }

  return (
    <div className="flex items-start gap-6">
      <ChartContainer config={chartConfig} className="h-[240px] w-[240px] flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <ChartTooltip content={<ChartTooltipContent />} />
            <Pie
              data={data as unknown as Record<string, unknown>[]}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={100}
              strokeWidth={2}
            >
              {data.map((entry, idx) => (
                <Cell
                  key={entry.label}
                  fill={CLASSIFICATION_COLORS[idx % CLASSIFICATION_COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>

      <div className="flex flex-col gap-3 min-w-0 flex-1">
        {data.map((item, idx) => {
          const perf = perfMap.get(item.label);
          return (
            <div key={item.label} className="space-y-1">
              <div className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor:
                      CLASSIFICATION_COLORS[idx % CLASSIFICATION_COLORS.length],
                  }}
                />
                <span className="text-sm text-foreground truncate flex-1">
                  {item.label}
                </span>
                <span className="text-sm font-medium tabular-nums">
                  {item.count.toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
                  {item.pct}%
                </span>
              </div>
              {perf && <OutcomeBar perf={perf} />}
            </div>
          );
        })}

        {categoryPerformance && categoryPerformance.length > 0 && (
          <div className="flex items-center gap-3 pt-1 border-t">
            {OUTCOME_KEYS.map(({ outcome }) => (
              <div key={outcome} className="flex items-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: REPLY_OUTCOME_CONFIG[outcome].color }}
                />
                <span className="text-xs text-muted-foreground">
                  {REPLY_OUTCOME_CONFIG[outcome].label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PERF_COLUMNS: { key: PerfSortKey; label: string; className?: string }[] = [
  { key: "category", label: "Category" },
  { key: "total", label: "Total" },
  { key: "autoSent", label: "Auto-Sent" },
  { key: "draft", label: "Draft" },
  { key: "handover", label: "Escalation" },
  { key: "skipped", label: "Skipped" },
  { key: "avgConfidence", label: "Avg Confidence" },
];

function PerfSortIcon({ column, sortKey, sortDir }: { column: PerfSortKey; sortKey: PerfSortKey; sortDir: SortDir }) {
  if (column !== sortKey) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return sortDir === "asc"
    ? <ArrowUp className="w-3 h-3" />
    : <ArrowDown className="w-3 h-3" />;
}

const CELL_COLORS: Partial<Record<PerfSortKey, string>> = {
  autoSent: "text-green-600",
  draft: "text-yellow-600",
  handover: "text-orange-600",
  skipped: "text-gray-500",
};

function TableView({ data }: { data: CategoryPerformanceRow[] }) {
  const [sortKey, setSortKey] = useState<PerfSortKey>("total");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  function toggleSort(key: PerfSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <div className="-mx-6 -mb-6 max-h-[450px] overflow-auto">
      <table className="w-full caption-bottom text-sm">
        <thead className="sticky top-0 z-10 bg-muted [&_tr]:border-b">
          <tr className="border-b">
            {PERF_COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`h-12 px-4 align-middle font-medium text-muted-foreground select-none cursor-pointer hover:text-foreground transition-colors ${
                  col.key === "category" ? "text-left" : "text-right"
                }`}
                onClick={() => toggleSort(col.key)}
              >
                <span className={`inline-flex items-center gap-1 ${col.key !== "category" ? "justify-end" : ""}`}>
                  {col.label}
                  <PerfSortIcon column={col.key} sortKey={sortKey} sortDir={sortDir} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {sorted.map((row) => (
            <tr key={row.category} className="border-b transition-colors hover:bg-muted/50">
              <td className="p-4 align-middle">
                <Badge variant="outline" className="font-medium">
                  {row.category}
                </Badge>
              </td>
              <td className="p-4 align-middle text-right tabular-nums font-medium">
                {row.total.toLocaleString()}
              </td>
              <td className={`p-4 align-middle text-right tabular-nums ${CELL_COLORS.autoSent}`}>
                {row.autoSent.toLocaleString()}
              </td>
              <td className={`p-4 align-middle text-right tabular-nums ${CELL_COLORS.draft}`}>
                {row.draft.toLocaleString()}
              </td>
              <td className={`p-4 align-middle text-right tabular-nums ${CELL_COLORS.handover}`}>
                {row.handover.toLocaleString()}
              </td>
              <td className={`p-4 align-middle text-right tabular-nums ${CELL_COLORS.skipped}`}>
                {row.skipped.toLocaleString()}
              </td>
              <td className="p-4 align-middle text-right tabular-nums">
                {(row.avgConfidence * 100).toFixed(0)}<span>%</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ClassificationBreakdown({ data, categoryPerformance }: ClassificationBreakdownProps) {
  const [view, setView] = useState<"chart" | "table">("chart");

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-medium">
              Classification Breakdown
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Email distribution by classification label with reply outcome breakdown
            </p>
          </div>
          {categoryPerformance && categoryPerformance.length > 0 && (
            <div className="flex items-center border rounded-md">
              <Button
                variant={view === "chart" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 px-2.5 rounded-r-none"
                onClick={() => setView("chart")}
              >
                <PieChartIcon className="w-4 h-4" />
              </Button>
              <Button
                variant={view === "table" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 px-2.5 rounded-l-none"
                onClick={() => setView("table")}
              >
                <TableIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div
            className={`transition-all duration-300 ease-in-out ${
              view === "chart"
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 absolute inset-0 pointer-events-none"
            }`}
          >
            <ChartView data={data} categoryPerformance={categoryPerformance} />
          </div>
          <div
            className={`transition-all duration-300 ease-in-out ${
              view === "table"
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 absolute inset-0 pointer-events-none"
            }`}
          >
            {categoryPerformance && <TableView data={categoryPerformance} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
